import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  HeartPulse,
  Bell,
  MessageSquare,
  Pill,
} from 'lucide-react'
import { useAgentStore } from '../store/useAgentStore'
import { fetcher } from '../api/client'

interface DashboardStats {
  total_elderly: number
  active_alerts: number
  critical_alerts: number
  today_medications: number
  today_medications_taken: number
  recent_conversations: number
  health_status: string
  last_activity: string | null
}

export default function Dashboard() {
  const { t } = useTranslation()
  const { selectedElderlyId } = useAgentStore()

  const { data: stats, isLoading } = useQuery<DashboardStats>({
    queryKey: ['dashboard', selectedElderlyId],
    queryFn: () => fetcher(`/profiles/dashboard/stats${selectedElderlyId ? `?elderly_id=${selectedElderlyId}` : ''}`),
    enabled: !!selectedElderlyId,
  })

  const statCards = [
    {
      label: t('dashboard.health_status'),
      value: stats?.health_status ? t(`health.${stats.health_status}`) : '-',
      icon: HeartPulse,
      color: 'text-success-600 bg-success-50 dark:bg-success-900/20',
    },
    {
      label: t('dashboard.active_alerts'),
      value: stats?.active_alerts ?? 0,
      icon: Bell,
      color: stats?.critical_alerts ? 'text-danger-600 bg-danger-50 dark:bg-danger-900/20' : 'text-warning-600 bg-warning-50 dark:bg-warning-900/20',
    },
    {
      label: t('dashboard.medications'),
      value: `${stats?.today_medications_taken ?? 0} / ${stats?.today_medications ?? 0}`,
      icon: Pill,
      color: 'text-primary-600 bg-primary-50 dark:bg-primary-900/20',
    },
    {
      label: t('dashboard.conversations'),
      value: stats?.recent_conversations ?? 0,
      icon: MessageSquare,
      color: 'text-purple-600 bg-purple-50 dark:bg-purple-900/20',
    },
  ]

  if (!selectedElderlyId) {
    return <CreateProfilePrompt />
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {t('dashboard.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {t('dashboard.subtitle')}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card) => (
          <div key={card.label} className="card flex items-center gap-4">
            <div className={`p-4 rounded-2xl ${card.color}`}>
              <card.icon size={28} />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{card.label}</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {isLoading ? '...' : card.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Health Trend Mini */}
        <div className="card lg:col-span-2">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t('dashboard.health_trend')}
          </h3>
          <div className="h-48 flex items-center justify-center text-gray-400">
            {/* TODO: Add Recharts line chart */}
            <p>{t('common.coming_soon')}</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card space-y-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t('dashboard.quick_actions')}
          </h3>
          <button className="w-full btn-primary flex items-center justify-center gap-2">
            <MessageSquare size={20} />
            {t('actions.send_message')}
          </button>
          <button className="w-full btn-primary bg-success-600 hover:bg-success-700 flex items-center justify-center gap-2">
            <HeartPulse size={20} />
            {t('actions.check_vitals')}
          </button>
          <button className="w-full btn-primary bg-warning-600 hover:bg-warning-700 flex items-center justify-center gap-2">
            <Bell size={20} />
            {t('actions.view_alerts')}
          </button>
        </div>
      </div>
    </div>
  )
}

interface ElderlyProfileOpt {
  id: number
  full_name: string
  nickname?: string | null
}

function CreateProfilePrompt() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const setSelectedElderlyId = useAgentStore((s) => s.setSelectedElderlyId)
  const [name, setName] = useState('')
  const [nickname, setNickname] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: profiles } = useQuery<ElderlyProfileOpt[]>({
    queryKey: ['profiles'],
    queryFn: () => fetcher<ElderlyProfileOpt[]>('/profiles'),
  })

  const handleCreate = async () => {
    if (!name.trim()) return
    setCreating(true)
    setError(null)
    try {
      const { apiClient } = await import('../api/client')
      const res = await apiClient.post('/profiles', {
        full_name: name.trim(),
        nickname: nickname.trim() || undefined,
      })
      setSelectedElderlyId(res.data.id)
      queryClient.invalidateQueries({ queryKey: ['profiles'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    } catch {
      setError('Failed to create profile')
    } finally {
      setCreating(false)
    }
  }

  if (profiles && profiles.length > 0) {
    return (
      <div className="text-center py-20">
        <p className="text-xl text-gray-600 dark:text-gray-300 mb-4">
          {t('dashboard.select_elderly_prompt')}
        </p>
        <p className="text-sm text-gray-400">↑ Use the profile selector in the sidebar</p>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto py-12">
      <div className="card space-y-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white text-center">
          {t('dashboard.no_elderly_selected')}
        </h2>
        <p className="text-center text-gray-500 dark:text-gray-400">
          Create the first elderly profile to get started
        </p>
        <div>
          <label className="label">Full name</label>
          <input className="input-field" value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Ahmed Al-Saeed" aria-label="Full name" />
        </div>
        <div>
          <label className="label">Nickname (optional)</label>
          <input className="input-field" value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="e.g. Baba" aria-label="Nickname" />
        </div>
        {error && <p role="alert" className="text-danger-600 font-bold text-center">{error}</p>}
        <button className="btn-primary w-full" onClick={() => handleCreate()}
          disabled={creating || !name.trim()}>
          {creating ? 'Creating…' : 'Create Profile'}
        </button>
      </div>
    </div>
  )
}
